import React, { useCallback, useState } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { Keyboard } from 'react-native';

import ActionButton from 'components/action-button';
import { CardModal } from 'components/card-modal';
import { toaster } from 'components/toaster/toaster';
import { withTypography } from 'components/ui';
import { useLoyaltyUpdateIdAndPayMutation } from 'generated/graphql-gateway';
import useErrorModal from 'hooks/use-error-modal';
import { t } from 'state/intl/translations';
import { TranslationKey } from 'state/intl/types';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { getDesignToken } from 'styles/constants';
import { toError } from 'utils/errors';
import logger from 'utils/logger';

import { useCardNumber } from '../../../hooks/use-giftcard-number';
import { usePaymentContext } from '../../../state';
import { CurrentSelectedMethodType } from '../../../types';
import { AddGiftCardInput } from '../../add-gift-card-input';
import GiftCardImage from '../gift-card-image';

const GIFT_CARD_NUMBER_TRANSLATION_ID = 'giftCardNumber';

export const AddGiftCardModal: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const {
    defaultPaymentMethodId,
    getPrepaidPaymentMethod,
    mergePrepaidCardBalances,
    setCheckoutPaymentMethodId,
  } = usePaymentContext();

  const {
    giftCardNumber,
    errors,
    isValidGiftCardNumber,
    handleCardNumberChange,
    validateCardNumber,
  } = useCardNumber();
  const { loyaltyUser } = useLoyaltyUser();
  const [loyaltyQuickPayMutation] = useLoyaltyUpdateIdAndPayMutation();
  const router = useRouter();
  const isDigitalGiftCardNumberExposedEnabled = useFlag(
    LaunchDarklyFlag.IS_DIGITAL_GIFT_CARD_NUMBER_EXPOSED_ENABLED
  );

  const handleCloseModal = () => {
    setCheckoutPaymentMethodId(defaultPaymentMethodId || CurrentSelectedMethodType.ADD_NEW_CARD);
    router.dismiss();
  };

  const [ErrorDialog, openErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Vaulting Gift Card',
    onCancel: handleCloseModal,
  });

  const addNewCard = useCallback(
    async (event: any) => {
      Keyboard.dismiss();
      event.preventDefault();

      setIsLoading(true);

      if (getPrepaidPaymentMethod()) {
        return openErrorDialog({ message: t('giftCardExists') });
      }

      try {
        const { storedPaymentMethodId } = await mergePrepaidCardBalances({
          // Source pin is not required, BK crown cards don't have it
          sourceCardNumber: giftCardNumber,
        });
        loyaltyQuickPayMutation({
          variables: {
            idAndPayInput: { storedPaymentMethodId, enabled: true },
            loyaltyId: loyaltyUser?.id,
          },
        });

        setCheckoutPaymentMethodId(storedPaymentMethodId);
        if (isDigitalGiftCardNumberExposedEnabled) {
          toaster.giftCardAddedToast();
        }
        router.dismiss();
      } catch (error) {
        openErrorDialog({ message: t('giftCardAddingError'), error: toError(error) });
        logger.error({ error, message: 'Error adding gift card' });
      } finally {
        setIsLoading(false);
      }
    },
    [
      getPrepaidPaymentMethod,
      openErrorDialog,
      mergePrepaidCardBalances,
      giftCardNumber,
      loyaltyQuickPayMutation,
      loyaltyUser?.id,
      setCheckoutPaymentMethodId,
      router,
      isDigitalGiftCardNumberExposedEnabled,
    ]
  );

  const submitButtonText = `${t('save')} & ${t('continue')}`;
  const handleGiftCardNumberChange = useCallback(
    (cardNumber: string) => {
      handleCardNumberChange(cardNumber);
      validateCardNumber(cardNumber);
    },
    [handleCardNumberChange, validateCardNumber]
  );

  return (
    <CardModal
      analyticsEvent="Add gift card"
      testID="add-giftcard-form"
      onDismiss={handleCloseModal}
      standardPadding
      keyboardAwareFooter
      headerText={t('addNewPaymentMethodGiftCard')}
      footer={
        <ActionButton
          fullWidth
          testID="save-and-continue"
          isLoading={isLoading}
          disabled={!isValidGiftCardNumber}
          onPress={addNewCard}
        >
          {submitButtonText}
        </ActionButton>
      }
    >
      <GiftCardImage />
      <AddGiftCardInput
        onChange={handleGiftCardNumberChange}
        errorMessage={errors?.giftCardNumber}
        label={t(GIFT_CARD_NUMBER_TRANSLATION_ID as TranslationKey)}
        testID="add-giftcard-input"
      />
      <ErrorDialog />
    </CardModal>
  );
};

export const Info = withTypography({ my: '$0', mr: '$0', ml: '$4', variant: 'copyOne' });

export const SuccessIcon = Icon.withConfig({
  variant: 'bkCrown',
  size: '$12',
  alignSelf: 'center',
  color: getDesignToken('crown-color'),
});
